import React, { useEffect, useMemo, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import Message from '../../components/ui/Message';
import { errorMessage } from '../../utils/access';
import { featureIcon, formatMoney } from './featureUi';

// Licenciamiento mensual de un banco (todo en dólares)
interface LicenseTier { from: number; to: number | null; basePrice: number; }
interface BankLicense {
    includedMerchants: number;      // cubiertos por el fee de plataforma
    platformFee: number | null;
    invoiceBlockSize: number;       // facturación electrónica: documentos por bloque
    readBlockSize: number;          // contabilidad: facturas leídas por bloque
    tiers: LicenseTier[];
}

// Módulo del banco: asociado y su cargo adicional por comercio
interface LicenseFeature {
    id: number;
    code: string;
    name: string;
    description: string | null;
    base: boolean;
    enabled: boolean;
    bankCost: number | null;
    defaultCost: number | null;
}

interface LicenseResponse {
    bank: { id: number; name: string; countryId: number };
    configured: boolean;
    license: BankLicense;
    features: LicenseFeature[];
}

interface TierDraft { to: string; price: string; }
interface ModuleDraft { enabled: boolean; cost: string; }
interface Draft {
    included: string;
    fee: string;
    blockSize: string;
    readBlockSize: string;
    tiers: TierDraft[];
    modules: Record<string, ModuleDraft>;
}

const FEATURE_ELECTRONIC_INVOICE = 'electronic-invoice';
const FEATURE_ACCOUNTING = 'accounting';

const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

const toDraft = (data: LicenseResponse): Draft => ({
    included: text(data.license.includedMerchants),
    fee: text(data.license.platformFee),
    blockSize: text(data.license.invoiceBlockSize),
    readBlockSize: text(data.license.readBlockSize),
    tiers: data.license.tiers.map(t => ({ to: text(t.to), price: text(t.basePrice) })),
    modules: Object.fromEntries(data.features.filter(f => !f.base).map(f => [f.code, {
        enabled: f.enabled,
        cost: text(f.bankCost ?? (f.enabled ? null : f.defaultCost)),
    }])),
});

const isAmount = (value: string, decimals = 2) => new RegExp(`^\\d{1,8}(\\.\\d{1,${decimals}})?$`).test(value.trim());
const isCount = (value: string, min: number) => /^\d{1,8}$/.test(value.trim()) && Number(value) >= min;
const fmt = (n: number) => n.toLocaleString('es-CR');

// "Desde" de cada tramo: 1 el primero; los demás, el siguiente del "hasta" anterior
const tierStarts = (tiers: TierDraft[]) => tiers.reduce<number[]>((starts, tier, i) => {
    if (i === 0) return [1];
    const previous = Number(tiers[i - 1].to);
    return [...starts, Number.isFinite(previous) && tiers[i - 1].to.trim() ? previous + 1 : NaN];
}, []);

// Errores del tramo i ('' si está bien)
const tierError = (tiers: TierDraft[], i: number) => {
    const tier = tiers[i];
    const last = i === tiers.length - 1;
    const from = tierStarts(tiers)[i];
    if (!isAmount(tier.price)) return 'Precio con hasta 2 decimales';
    if (!last && !tier.to.trim()) return 'Indique "hasta" (solo el último queda abierto)';
    if (tier.to.trim() && (!isCount(tier.to, 1) || Number(tier.to) < from)) return `"Hasta" debe ser ${Number.isFinite(from) ? `${fmt(from)} o más` : 'un número'}`;
    return '';
};

/**
 * Licenciamiento por banco (plataforma). Lo que cobramos a cada entidad financiera al mes, en dólares:
 *   - fee de plataforma: cubre los primeros N comercios activos
 *   - licencia Base por volumen: el tramo lo determina el total de comercios activos del banco en el mes y su
 *     precio aplica a todas las licencias que exceden las incluidas
 *   - módulos: cargo adicional por comercio activo que lo usa, incluidos los cubiertos por el fee
 * "SwiftMart Full" no es un producto: es la Base más todos los módulos (se muestra calculado).
 */
export const BankLicensePage = () => {
    const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
    const [bankId, setBankId] = useState('');
    const [data, setData] = useState<LicenseResponse | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        topApi.get<{ banks: { id: number; name: string; status?: string }[] }>('/bank/getBanks')
            .then(resp => setBanks((resp.data.banks ?? []).filter(b => b.status !== 'INA').map(b => ({ id: b.id, name: b.name }))))
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        if (!bankId) { setData(null); setDraft(null); return; }
        setLoading(true);
        topApi.get<LicenseResponse>('/features/license', { params: { bankId } })
            .then(resp => { setData(resp.data); setDraft(toDraft(resp.data)); })
            .catch(e => setError(errorMessage(e)))
            .finally(() => setLoading(false));
    }, [bankId]);

    const modules = useMemo(() => (data?.features ?? []).filter(f => !f.base), [data]);
    const enabledModules = modules.filter(f => draft?.modules[f.code]?.enabled);

    const changed = useMemo(() => !!data && !!draft && JSON.stringify(toDraft(data)) !== JSON.stringify(draft), [data, draft]);

    const errors = useMemo(() => {
        if (!draft) return {} as Record<string, string>;
        const list: Record<string, string> = {};
        if (!isCount(draft.included, 0)) list.included = 'Número entero';
        if (draft.fee.trim() && !isAmount(draft.fee)) list.fee = 'Monto con hasta 2 decimales';
        if (!isCount(draft.blockSize, 1)) list.blockSize = 'Número entero mayor a 0';
        if (!isCount(draft.readBlockSize, 1)) list.readBlockSize = 'Número entero mayor a 0';
        if (!draft.tiers.length) list.tiers = 'Defina al menos un tramo';
        draft.tiers.forEach((_, i) => { const e = tierError(draft.tiers, i); if (e) list[`tier-${i}`] = e; });
        modules.forEach(f => {
            const m = draft.modules[f.code];
            if (m?.enabled && !isAmount(m.cost)) list[`module-${f.code}`] = 'Indique el cargo (hasta 2 decimales)';
        });
        return list;
    }, [draft, modules]);
    const invalid = Object.keys(errors).length > 0;

    const set = (patch: Partial<Draft>) => setDraft(prev => (prev ? { ...prev, ...patch } : prev));
    const setTier = (i: number, patch: Partial<TierDraft>) =>
        setDraft(prev => (prev ? { ...prev, tiers: prev.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) } : prev));
    const setModule = (code: string, patch: Partial<ModuleDraft>) =>
        setDraft(prev => (prev ? { ...prev, modules: { ...prev.modules, [code]: { ...prev.modules[code], ...patch } } } : prev));

    const addTier = () => setDraft(prev => {
        if (!prev) return prev;
        const tiers = [...prev.tiers];
        // El último deja de ser abierto: se le sugiere un "hasta"
        const starts = tierStarts(tiers);
        if (tiers.length && !tiers[tiers.length - 1].to.trim()) {
            const from = starts[tiers.length - 1];
            tiers[tiers.length - 1] = { ...tiers[tiers.length - 1], to: String((Number.isFinite(from) ? from : 1) + 499) };
        }
        return { ...prev, tiers: [...tiers, { to: '', price: tiers.length ? tiers[tiers.length - 1].price : '' }] };
    });
    const removeTier = (i: number) => setDraft(prev => {
        if (!prev) return prev;
        const tiers = prev.tiers.filter((_, j) => j !== i);
        if (tiers.length) tiers[tiers.length - 1] = { ...tiers[tiers.length - 1], to: '' };
        return { ...prev, tiers };
    });

    const save = async () => {
        if (!data || !draft || invalid || !changed) return;
        setSaving(true);
        try {
            const original = toDraft(data);
            const starts = tierStarts(draft.tiers);
            const resp = await topApi.put<LicenseResponse>('/features/license', {
                bankId: data.bank.id,
                includedMerchants: Number(draft.included),
                platformFee: draft.fee.trim() ? Number(draft.fee) : null,
                invoiceBlockSize: Number(draft.blockSize),
                readBlockSize: Number(draft.readBlockSize),
                tiers: draft.tiers.map((t, i) => ({ from: starts[i], to: t.to.trim() ? Number(t.to) : null, basePrice: Number(t.price) })),
                features: modules
                    .filter(f => JSON.stringify(original.modules[f.code]) !== JSON.stringify(draft.modules[f.code]))
                    .map(f => {
                        const m = draft.modules[f.code];
                        return { code: f.code, enabled: m.enabled, bankCost: m.enabled ? Number(m.cost) : null };
                    }),
            });
            setData(resp.data);
            setDraft(toDraft(resp.data));
            setSuccess('Licenciamiento guardado. Aplica al reporte Cobro por banco.');
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const numberInput = (label: string, value: string, onChange: (v: string) => void, opts: { error?: string; prefix?: string; width?: number; placeholder?: string; id?: string } = {}) => (
        <div className="input-group input-group-sm has-validation" style={{ width: opts.width ?? 150 }}>
            {opts.prefix && <span className="input-group-text">{opts.prefix}</span>}
            <input id={opts.id} className={`form-control text-end ${opts.error ? 'is-invalid' : ''}`} inputMode="decimal" aria-label={label}
                value={value} placeholder={opts.placeholder} disabled={saving} onChange={e => onChange(e.target.value)} />
            {opts.error && <div className="invalid-feedback">{opts.error}</div>}
        </div>
    );

    // Precio de cada combinación por tramo: Base + módulo; Full = Base + todos los módulos asociados
    const cost = (code: string) => {
        const value = Number(draft?.modules[code]?.cost);
        return Number.isFinite(value) ? value : 0;
    };
    const fullAddOn = enabledModules.reduce((sum, f) => sum + cost(f.code), 0);
    const preview = (price: string, addOn: number) => {
        const base = Number(price);
        return price.trim() && Number.isFinite(base) ? formatMoney(base + addOn, 'USD') : '-';
    };

    const moduleRule = (f: LicenseFeature) => {
        if (!draft) return null;
        if (f.code === FEATURE_ELECTRONIC_INVOICE) return (
            <div className="d-flex flex-wrap align-items-center gap-2 small mt-2">
                <span>Cargo por cada bloque de</span>
                {numberInput('Documentos por bloque', draft.blockSize, v => set({ blockSize: v }), { error: errors.blockSize, width: 110 })}
                <span>documentos emitidos por comercio al mes (mínimo un bloque).</span>
            </div>
        );
        if (f.code === FEATURE_ACCOUNTING) return (
            <div className="d-flex flex-wrap align-items-center gap-2 small mt-2">
                <span>Cargo por cada bloque de</span>
                {numberInput('Facturas leídas por bloque', draft.readBlockSize, v => set({ readBlockSize: v }), { error: errors.readBlockSize, width: 110 })}
                <span>facturas leídas por comercio al mes (mínimo un bloque).</span>
            </div>
        );
        return null;
    };

    const disclaimer = () => {
        if (!draft) return null;
        const blockSize = Number(draft.blockSize) || 0;
        const readBlockSize = Number(draft.readBlockSize) || 0;
        const parts = enabledModules.map(f => {
            const amount = formatMoney(cost(f.code), 'USD');
            if (f.code === FEATURE_ELECTRONIC_INVOICE) return `${f.name} (+${amount} por cada bloque de ${fmt(blockSize)} documentos emitidos)`;
            if (f.code === FEATURE_ACCOUNTING) return `${f.name} (+${amount} por cada bloque de ${fmt(readBlockSize)} facturas leídas)`;
            return `${f.name} (+${amount})`;
        });
        return (
            <p className="small text-muted mb-0">
                Precios en US$ por comercio activo al mes. SwiftMart Full no es un producto: es la Base más {enabledModules.length ? 'todos los módulos asociados' : 'los módulos'}.
                {' '}La escala aplica únicamente a la licencia Base y se cobra a partir del comercio {fmt((Number(draft.included) || 0) + 1)}
                {draft.fee.trim() ? `; los primeros ${fmt(Number(draft.included) || 0)} los cubre el fee de plataforma (${formatMoney(Number(draft.fee), 'USD')} al mes)` : ''}.
                {parts.length > 0 && <> {parts.join(', ')} se cobran sobre todos los comercios activos que utilicen el módulo, incluidos los cubiertos por el fee de plataforma.</>}
            </p>
        );
    };

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
                <div>
                    <h3 className="mb-1">Funcionalidades</h3>
                    <p className="text-muted mb-0">Licenciamiento mensual de cada banco: fee de plataforma, escala por volumen de la licencia Base y módulos, en dólares.</p>
                </div>
                <select className="form-select" style={{ maxWidth: 280 }} aria-label="Banco" value={bankId} onChange={e => setBankId(e.target.value)}>
                    <option value="">Seleccione el banco...</option>
                    {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
            ) : !data || !draft ? (
                <div className="card border-0 shadow-sm"><div className="card-body text-muted text-center py-5">Seleccione un banco para ver su licenciamiento.</div></div>
            ) : (<>
                {!data.configured && (
                    <div className="alert alert-warning small py-2">
                        <i className="bi bi-exclamation-triangle me-1" />
                        {data.bank.name} aún no tiene licenciamiento. Defina los tramos y guarde para que el cobro por banco lo calcule.
                    </div>
                )}

                {/* MÓDULOS */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                        <h6 className="mb-1">{data.bank.name} · Módulos</h6>
                        <p className="small text-muted">Primero defina el cargo de cada módulo: se suma a la licencia Base de cada tramo. Se cobra por comercio activo que utiliza el módulo, incluidos los cubiertos por el fee de plataforma. El banco solo puede ofrecer lo asociado.</p>
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr className="text-muted small text-uppercase">
                                        <th>Módulo</th>
                                        <th style={{ width: 110 }}>Asociado</th>
                                        <th style={{ width: 200 }}>Cargo adicional (USD)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modules.map(f => {
                                        const m = draft.modules[f.code];
                                        const id = `enabled-${f.code}`;
                                        return (
                                            <tr key={f.code} className={m?.enabled ? '' : 'text-muted'}>
                                                <td>
                                                    <div className="d-flex align-items-start gap-3">
                                                        <span className="text-primary fs-5"><i className={`bi ${featureIcon(f.code)}`} /></span>
                                                        <div className="flex-grow-1">
                                                            <div className="fw-medium">{f.name}</div>
                                                            {f.description && <div className="small text-muted">{f.description}</div>}
                                                            {m?.enabled && moduleRule(f)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="form-check form-switch mb-0">
                                                        <input id={id} className="form-check-input" type="checkbox" role="switch" checked={!!m?.enabled} disabled={saving}
                                                            onChange={e => setModule(f.code, { enabled: e.target.checked })} />
                                                        <label className="form-check-label small" htmlFor={id}>{m?.enabled ? 'Sí' : 'No'}</label>
                                                    </div>
                                                </td>
                                                <td>
                                                    {numberInput(`Cargo adicional de ${f.name}`, m?.cost ?? '', v => setModule(f.code, { cost: v }),
                                                        { error: errors[`module-${f.code}`], prefix: '$', width: 170 })}
                                                    <div className="small text-muted mt-1">
                                                        {f.code === FEATURE_ELECTRONIC_INVOICE ? `por bloque de ${fmt(Number(draft.blockSize) || 0)} documentos`
                                                            : f.code === FEATURE_ACCOUNTING ? `por bloque de ${fmt(Number(draft.readBlockSize) || 0)} facturas leídas`
                                                            : 'por comercio al mes'}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* FEE DE PLATAFORMA */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                        <h6 className="mb-3">Fee de plataforma</h6>
                        <div className="d-flex flex-wrap gap-4">
                            <div>
                                <label className="form-label small text-muted mb-1" htmlFor="license-included">Comercios incluidos</label>
                                {numberInput('Comercios incluidos', draft.included, v => set({ included: v }), { error: errors.included, id: 'license-included', width: 140 })}
                            </div>
                            <div>
                                <label className="form-label small text-muted mb-1" htmlFor="license-fee">Fee mensual (USD)</label>
                                {numberInput('Fee de plataforma', draft.fee, v => set({ fee: v }), { error: errors.fee, prefix: '$', id: 'license-fee', width: 170, placeholder: 'No se cobra' })}
                            </div>
                        </div>
                        <div className="small text-muted mt-2">La licencia Base se cobra a partir del comercio {fmt((Number(draft.included) || 0) + 1)}.</div>
                    </div>
                </div>

                {/* ESCALA POR VOLUMEN */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-1">
                            <h6 className="mb-0">Licencia Base por volumen</h6>
                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={saving} onClick={addTier}>
                                <i className="bi bi-plus-lg me-1" /> Tramo
                            </button>
                        </div>
                        <p className="small text-muted">
                            El tramo se determina por la cantidad total de comercios activos del banco en el mes, y su precio aplica a todas las licencias que exceden las {fmt(Number(draft.included) || 0)} incluidas.
                        </p>
                        {errors.tiers && <div className="text-danger small mb-2">{errors.tiers}</div>}
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr className="text-muted small text-uppercase">
                                        <th>Comercios activos en el mes</th>
                                        <th className="text-end">SwiftMart Base</th>
                                        {enabledModules.map(f => <th key={f.code} className="text-end">+ {f.name}</th>)}
                                        {enabledModules.length > 1 && <th className="text-end">SwiftMart Full</th>}
                                        <th style={{ width: 48 }} />
                                    </tr>
                                </thead>
                                <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {draft.tiers.map((tier, i) => {
                                        const from = tierStarts(draft.tiers)[i];
                                        const last = i === draft.tiers.length - 1;
                                        const err = errors[`tier-${i}`];
                                        const priceError = !!err && err.startsWith('Precio');
                                        return (
                                            <tr key={i}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-nowrap" style={{ minWidth: 60 }}>{Number.isFinite(from) ? fmt(from) : '?'}</span>
                                                        <span className="text-muted">–</span>
                                                        <input className={`form-control form-control-sm text-end ${err && !priceError ? 'is-invalid' : ''}`}
                                                            style={{ width: 110 }} inputMode="numeric" aria-label={`Hasta del tramo ${i + 1}`}
                                                            placeholder={last ? 'o más' : ''} value={tier.to} disabled={saving}
                                                            onChange={e => setTier(i, { to: e.target.value })} />
                                                    </div>
                                                    {err && !priceError && <div className="small text-danger mt-1">{err}</div>}
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex justify-content-end">
                                                        {numberInput(`Precio Base del tramo ${i + 1}`, tier.price, v => setTier(i, { price: v }), { prefix: '$', width: 130, error: priceError ? err : undefined })}
                                                    </div>
                                                </td>
                                                {enabledModules.map(f => <td key={f.code} className="text-end text-muted">{preview(tier.price, cost(f.code))}</td>)}
                                                {enabledModules.length > 1 && <td className="text-end fw-medium">{preview(tier.price, fullAddOn)}</td>}
                                                <td className="text-end">
                                                    <button type="button" className="btn btn-sm btn-link text-danger" aria-label={`Quitar tramo ${i + 1}`}
                                                        disabled={saving || draft.tiers.length === 1} onClick={() => removeTier(i)}>
                                                        <i className="bi bi-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {disclaimer()}
                    </div>
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                    <span className="small text-muted">Se usa en el reporte Cobro por banco. Qué ofrece el banco a sus comercios y a qué precio lo define el banco.</span>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary" disabled={!changed || saving} onClick={() => setDraft(toDraft(data))}>
                            Descartar
                        </button>
                        <button type="button" className="btn btn-primary" disabled={!changed || invalid || saving} onClick={save}>
                            {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check2 me-1" />}
                            Guardar
                        </button>
                    </div>
                </div>
            </>)}

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Cambios guardados" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
